/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsOverlay} from '../../theme/theme';

/**
 * The page content a page scrim is bounded to - the application layout's own
 * root, which sits beside the main menu rather than under it. Covering that
 * box alone is what keeps the menu above the scrim; where it is not on the
 * page the scrim covers the whole window.
 */
export const PAGE_SCRIM_BOUNDS_ID = 'layout-root';

// the scrim is a plain sheet of colour, so it is addressed by test id ...
export const PAGE_SCRIM_TEST_ID = 'page-scrim';

// where the scrim's edges sit, as insets from the window's own ...
type ScrimInsets = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

// with nothing to bound it by, the scrim covers the whole window ...
const FULL_WINDOW: ScrimInsets = {top: 0, left: 0, right: 0, bottom: 0};

// the page content's own box, read live ...
const measureScrim = (): ScrimInsets => {
  const content = document.getElementById(PAGE_SCRIM_BOUNDS_ID);
  let insets    = FULL_WINDOW;
  if (content) {
    const {top, left, right, bottom} = content.getBoundingClientRect();
    insets = {
      top,
      left,
      right  : window.innerWidth - right,
      bottom : window.innerHeight - bottom
    };
  }
  return insets;
};

// a frame that measures the same box keeps the one it had, so nothing
// re-renders for it ...
const isSameInsets = (current: ScrimInsets, next: ScrimInsets): boolean => (
  current.top === next.top && current.left === next.left &&
  current.right === next.right && current.bottom === next.bottom
);

type StyleProps = {
  insets: ScrimInsets;
};

export interface PageScrimProps {
  // a click on the scrim - e.g. closing what it was opened behind. Without
  // one, the scrim simply swallows the click ...
  onClick?: () => void;
}

const useStyles = makeStyles(() => ({
  // carries no z-index of the page's own - it is ordered against whatever it
  // is rendered beside, inside the layer (e.g. a portal) it is rendered in ...
  scrim : {
    top             : ({insets}: StyleProps) => `${insets.top}px`,
    left            : ({insets}: StyleProps) => `${insets.left}px`,
    right           : ({insets}: StyleProps) => `${insets.right}px`,
    bottom          : ({insets}: StyleProps) => `${insets.bottom}px`,
    position        : 'fixed',
    zIndex          : 0,
    backgroundColor : () => dlsOverlay('black')
  }
}));

/**
 * Renders the current theme's page scrim - the page content dimmed behind a
 * panel or prompt opened over it, swallowing the clicks meant for it, while
 * the main menu beside it stays clear. It is drawn within the layer it is
 * rendered in, below a panel rendered after it there.
 *
 * The page content's box is followed every frame while the scrim is shown,
 * as it moves with things that are not all events - the main menu
 * collapsing, a sheet opening, the window resizing.
 *
 * ```typescript
 *
 *   import PageScrim from '@ion/dls/src/components/backdrop/PageScrim';
 *   ...
 *
 *   <IonPortal id='map-layer-chooser' zIndex={getTimeModePanelZIndex()}>
 *     <PageScrim />
 *     <LayerChooser {...layerChooserProps} />
 *   </IonPortal>
 *
 *   // closes the panel on a click outside it ...
 *   <PageScrim onClick={closePanel} />
 *
 * ```
 *
 * @param   onClick Invoked on a click on the scrim.
 * @returns         The page scrim.
 */
export const PageScrim = ({onClick}: PageScrimProps) => {
  const [insets, setInsets] = useState<ScrimInsets>(measureScrim);
  const classes             = useStyles({insets});

  useEffect(() => {
    let frame = 0;
    const track = function() {
      setInsets(function(current) {
        const next = measureScrim();
        return isSameInsets(current, next) ? current : next;
      });
      frame = window.requestAnimationFrame(track);
    };
    frame = window.requestAnimationFrame(track);
    return function() {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return <div className={classes.scrim} data-testid={PAGE_SCRIM_TEST_ID} onClick={onClick} />;
};

export default memo(PageScrim);
